import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import moment from "moment";
import { useNavigate, useLocation } from "react-router-dom";
import "./TripRegistrationForm.css";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import confirmDatePlugin from "flatpickr/dist/plugins/confirmDate/confirmDate";
import { translations } from './translations';
import { normalizeTruckNumber } from './tripFleet';

// normalize truck id by removing spaces/slashes and uppercasing (consistent with other normalization used in repo)
const normalizeTruckIdForDropdown = (id) => {
  if (!id && id !== 0) return '';
  return id.toString().toUpperCase().replace(/[^A-Z0-9]/g, '');
};

const ACTIVE_VIOLATION_WARNING =
  "Cannot register trip. This driver has an active violation that must be closed first.\nلا يمكن تسجيل الرحلة. لدى هذا السائق مخالفة نشطة يجب إغلاقها أولاً.";

const ACTIVE_DFMS_DEFECT_WARNING =
  "Cannot register trip. This truck has an active DFMS defect and DFMS is not working.\nلا يمكن تسجيل الرحلة. يوجد عطل نشط في نظام DFMS لهذه الشاحنة والنظام لا يعمل.";

const normalizeText = (value) => (value || "").toString().trim().toLowerCase();
const normalizeTruckText = (value) =>
  (value || "").toString().trim().toUpperCase().replace(/[^A-Z0-9]/g, "");

const getDriverNameFromLabel = (value) => (value || "").split(" (")[0].trim();

const normalizeViolationRecord = (record) => {
  const normalized = {};
  Object.keys(record || {}).forEach((key) => {
    normalized[key.toLowerCase()] = record[key];
  });
  return normalized;
};

const isViolationActive = (violation) => {
  const status = (violation.violation_status || "").toString().trim().toUpperCase();
  const resolved = (violation.resolved || "").toString().trim().toUpperCase();

  return status === "OPEN" || (!status && resolved !== "Y");
};

const findActiveViolationForDriver = (driver, violationsToSearch = []) => {
  if (!driver) return null;

  const driverName = normalizeText(driver.driver_name || driver.name);
  const driverCode = normalizeText(driver.reporting_driver_code);
  const driverId = normalizeText(driver.driver_id);

  return (
    violationsToSearch.find((violation) => {
      const violationDriverName = normalizeText(violation.driver_name);
      const violationDriverCode = normalizeText(violation.reporting_driver_code);
      const violationDriverId = normalizeText(violation.driver_id);

      return (
        (driverName && violationDriverName === driverName) ||
        (driverCode && violationDriverCode === driverCode) ||
        (driverId && violationDriverId === driverId)
      );
    }) || null
  );
};

const normalizeDfmsDefectRecord = (record) => {
  const normalized = {};
  Object.keys(record || {}).forEach((key) => {
    normalized[key.toLowerCase()] = record[key];
  });
  return normalized;
};

const isDfmsDefectActive = (defect) => {
  const status = (defect.defect_status || "").toString().trim().toUpperCase();
  const resolved = (defect.resolved || "").toString().trim().toUpperCase();
  return status === "OPEN" || (!status && resolved !== "Y");
};

const findActiveDfmsDefectForTruck = (truckId, defectsToSearch = []) => {
  const selectedTruck = normalizeTruckText(truckId);
  if (!selectedTruck) return null;

  return (
    defectsToSearch.find((defect) => {
      const defectTruck = normalizeTruckText(
        defect.scheduled_vehicle || defect.truck_id || defect.vehicle
      );
      return defectTruck && defectTruck === selectedTruck;
    }) || null
  );
};

const TripRegistrationForm = () => {
  const isSupervisor = localStorage.getItem("userrole") === "driver_supv";
  // helper to get tomorrow at local 06:00
  const getTomorrowSixAM = () => {
    return moment()
      .add(1, "day")
      .startOf("day")
      .hour(6)
      .minute(0)
      .second(0) 
      .toDate();
  };

  // helper to force minutes/seconds/ms to zero (hour-only)
  const normalizeToHour = (date) => {
    if (!date) return date;
    const d = new Date(date);
    d.setMinutes(0, 0, 0);
    return d;
  };

  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    driverId: "",
    driverLabel: "",
    truckId: "",
    carrier: "",
    registrationTime: moment().local().format("YYYY-MM-DDTHH:mm"),
    // default loading_time to tomorrow 06:00 local
    loading_time: getTomorrowSixAM(),
    tripStatus: "Active",
    remarks: "",
  });

  const [activeDrivers, setActiveDrivers] = useState([]);
  const [showVehicledata, setDropdownData] = useState({ scheduledVehicles: [], carriers: [] });
  const [fleetVehicles, setFleetVehicles] = useState([]);
  const findFleetTruck = (value) => fleetVehicles.find((item) => item.truck_key === normalizeTruckNumber(value)) || null;
  const getCarrierByTruck = (value) => findFleetTruck(value)?.company_name || "";
  const formatApprovedTruck = (value) => findFleetTruck(value)?.truck_reg || "";
  const getFleetTruck = findFleetTruck;
  const [showDriverDropdown, setShowDriverDropdown] = useState(false);
  const [driverSearchTerm, setDriverSearchTerm] = useState("");
  const [selectedDriverTemp, setSelectedDriverTemp] = useState(null);
  const [showTruckDropdown, setShowTruckDropdown] = useState(false);
  const [truckSearchTerm, setTruckSearchTerm] = useState("");
  const [selectedTruckTemp, setSelectedTruckTemp] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState("down");
  const [driverRestStatus, setDriverRestStatus] = useState(null);
  const [activeViolations, setActiveViolations] = useState([]);
  const [driverViolationStatus, setDriverViolationStatus] = useState(null);
  const [activeDfmsDefects, setActiveDfmsDefects] = useState([]);
  const [truckDfmsDefectStatus, setTruckDfmsDefectStatus] = useState(null);

  const getCurrentLocalDateTime = () =>
    moment().local().format("YYYY-MM-DDTHH:mm");

  useEffect(() => {
    const fetchDropdownData = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_API_URL}/api/trip-fleet`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        console.log("API Response:", response.data);

        const fleet = response.data.data || [];
        setFleetVehicles(fleet);
        const visibleFleet = isSupervisor ? fleet.filter((item) => item.company_name === "Al Madina Logistics") : fleet;
        setDropdownData({ scheduledVehicles: visibleFleet.map((item) => item.truck_reg), carriers: [] });
      } catch (error) {
        console.error("Error fetching dropdown data:", error);
        setDropdownData({ scheduledVehicles: [], carriers: [] });
      } finally {
        setLoading(false);
      }
    };
    const fetchAll = async () => {
      setLoading(true);
      const token = localStorage.getItem("token");
      const userRole = localStorage.getItem("userrole");
      const driverBadge = localStorage.getItem("badge");

      try {
        const drvRes = await axios.get(
          `${process.env.REACT_APP_BASE_API_URL}/drivers-list`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        let normalizedActiveViolations = [];
        try {
          const violationsRes = await axios.get(
            `${process.env.REACT_APP_BASE_API_URL}/driver-violations`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const violationRows = Array.isArray(violationsRes.data)
            ? violationsRes.data
            : violationsRes.data?.data || [];
          normalizedActiveViolations = violationRows
            .map(normalizeViolationRecord)
            .filter(isViolationActive);
        } catch (violationErr) {
          console.error("Error fetching active violations:", violationErr);
        }
        setActiveViolations(normalizedActiveViolations);

        let normalizedActiveDfmsDefects = [];
        try {
          const dfmsRes = await axios.get(
            `${process.env.REACT_APP_BASE_API_URL}/dfms-truck-defects`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const dfmsRows = Array.isArray(dfmsRes.data)
            ? dfmsRes.data
            : dfmsRes.data?.data || [];
          normalizedActiveDfmsDefects = dfmsRows
            .map(normalizeDfmsDefectRecord)
            .filter(isDfmsDefectActive);
        } catch (dfmsErr) {
          console.error("Error fetching active DFMS truck defects:", dfmsErr);
        }
        setActiveDfmsDefects(normalizedActiveDfmsDefects);

        const active = (drvRes.data.data || [])
          .filter((d) => d.is_active === "Y")
          .map((d) => ({
            driver_id: d.driver_id || "",
            driver_name: d.driver_name,
            reporting_driver_code: d.reporting_driver_code || "",
          }));
        setActiveDrivers(active);

        if (userRole === "driver" && driverBadge) {
          const currentDriver = active.find(
            (d) => d.reporting_driver_code === driverBadge
          );
          if (currentDriver) {
            setForm((prev) => ({
              ...prev,
              driverId: `${currentDriver.driver_name} (${currentDriver.reporting_driver_code})`,
              driverLabel: `${currentDriver.driver_name} (${currentDriver.reporting_driver_code})`,
              reporting_driver_code: currentDriver.reporting_driver_code,
              registrationTime:
                prev.registrationTime || getCurrentLocalDateTime(),
            }));
            // use current component state for loading_time (prev is not defined here)
            await checkDriverRestStatus(currentDriver.driver_name, form.loading_time || getTomorrowSixAM());
            const activeViolation = findActiveViolationForDriver(
              currentDriver,
              normalizedActiveViolations
            );
            setDriverViolationStatus(activeViolation);
          }
        } else {
          setForm((prev) => ({
            ...prev,
            registrationTime:
              prev.registrationTime || getCurrentLocalDateTime(),
          }));
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setActiveDrivers([]);
      } finally {
        setLoading(false);
      }
    };
    fetchDropdownData();
    fetchAll();
  }, []);

  useEffect(() => {
    const editTrip = location.state?.editTrip;
    if (editTrip) {
      setForm({
        driverId: editTrip.driver_id,
        driverLabel: editTrip.driver_id,
        truckId: formatApprovedTruck(editTrip.truck_id) || editTrip.truck_id,
        carrier: getCarrierByTruck(editTrip.truck_id),
        registrationTime: moment(editTrip.registration_time).format(
          "YYYY-MM-DDTHH:mm"
        ),
        loading_time: editTrip.loading_time
          ? normalizeToHour(moment(editTrip.loading_time).toDate())
          : normalizeToHour(getTomorrowSixAM()),
        remarks: editTrip.remarks || "",
        reporting_driver_code: editTrip.reporting_driver_code,
      });
    }
  }, [location.state?.editTrip, fleetVehicles]);

  useEffect(() => {
    const workflow = location.state;
    if (!workflow?.tripWorkflow || !workflow.vehicleChecklistId) return;
    const label = `${workflow.driverName} (${workflow.reportingDriverCode})`;
    setForm((previous) => ({
      ...previous,
      driverId: label,
      driverLabel: label,
      reporting_driver_code: workflow.reportingDriverCode,
      truckId: formatApprovedTruck(workflow.truckId),
      carrier: getCarrierByTruck(workflow.truckId),
    }));
    setTruckSearchTerm(workflow.truckId || "");
  }, [location.state, fleetVehicles]);

  const filteredDrivers = useMemo(() => {
    const searchTerm = driverSearchTerm.toLowerCase().trim();
    console.log("Search Term:", searchTerm);
    const filtered = activeDrivers.filter(
      (driver) =>
        driver.driver_name.toLowerCase().includes(searchTerm) ||
        (driver.reporting_driver_code &&
          driver.reporting_driver_code.toLowerCase().includes(searchTerm))
    );

    console.log("Filtered Drivers:", filtered);
    return filtered;
  }, [activeDrivers, driverSearchTerm]);

  const checkDriverRestStatus = async (driverName) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_API_URL}/api/driver-rest-status`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const statusMap = response.data.data;
      const driverStatus = statusMap[driverName];

      const needsRest = Boolean(driverStatus?.needs_rest ?? driverStatus?.needsRest);

      // No rest requirement
      const blocked = driverStatus?.is_expat === 'Y' &&
        (driverStatus.current_rest_status === 'ON_REST' || (needsRest && !driverStatus.has_active_override));
      if (!blocked) {
        setDriverRestStatus(null);
        return false;
      }

      // Block only Expat drivers; the backend repeats this eligibility check.
      setDriverRestStatus({
        needsRest: true,
        consecutiveWorkDays: driverStatus.consecutive_work_days ?? driverStatus.consecutiveWorkDays,
        lastRestDate: driverStatus.last_rest_date ?? driverStatus.lastRestDate,
      });
      return true;
    } catch (error) {
      console.error("Error checking driver rest status:", error);
      return false;
    }
  };

  const checkDriverActiveViolation = (driver) => {
    const activeViolation = findActiveViolationForDriver(driver, activeViolations);
    setDriverViolationStatus(activeViolation);
    return activeViolation;
  };

  const checkTruckActiveDfmsDefect = (truckId) => {
    const activeDefect = findActiveDfmsDefectForTruck(truckId, activeDfmsDefects);
    setTruckDfmsDefectStatus(activeDefect);
    return activeDefect;
  };

  useEffect(() => {
    if (!form.driverId) {
      setDriverViolationStatus(null);
      return;
    }

    const activeViolation = findActiveViolationForDriver(
      {
        driver_name: getDriverNameFromLabel(form.driverId),
        reporting_driver_code: form.reporting_driver_code,
      },
      activeViolations
    );
    setDriverViolationStatus(activeViolation);
  }, [activeViolations, form.driverId, form.reporting_driver_code]);

  useEffect(() => {
    if (!form.truckId) {
      setTruckDfmsDefectStatus(null);
      return;
    }

    const activeDefect = findActiveDfmsDefectForTruck(
      form.truckId,
      activeDfmsDefects
    );
    setTruckDfmsDefectStatus(activeDefect);
  }, [activeDfmsDefects, form.truckId]);

  const handleDriverSelection = async () => {
    console.log("Selected Driver Temp:", selectedDriverTemp);

    if (selectedDriverTemp) {
      await checkDriverRestStatus(
        selectedDriverTemp.driver_name,
        form.loading_time
      );

      const activeViolation = checkDriverActiveViolation(selectedDriverTemp);

      if (activeViolation) {
        alert(ACTIVE_VIOLATION_WARNING);
        setSelectedDriverTemp(null);
        return;
      }

      setForm((prev) => ({
        ...prev,
        driverId: `${selectedDriverTemp.driver_name} (${
          selectedDriverTemp.reporting_driver_code || ""
        })`,
        driverLabel: `${selectedDriverTemp.driver_name} (${
          selectedDriverTemp.reporting_driver_code || ""
        })`,
        reporting_driver_code: selectedDriverTemp.reporting_driver_code || "",
      }));
    }
    setShowDriverDropdown(false);
    setDriverSearchTerm("");
    setSelectedDriverTemp(null);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!location.state?.editTrip && !location.state?.vehicleChecklistId) {
      alert("A passing vehicle checklist is mandatory. Please start from New Trip.");
      navigate("/driver-checklists/new", { replace: true, state: { tripWorkflow: true } });
      return;
    }
    if (!form.driverId || !form.truckId) {
      alert("Please select driver and truck");
      return;
    }

    const approvedTruck = getFleetTruck(form.truckId);
    if (isSupervisor && findFleetTruck(form.truckId)?.company_name !== "Al Madina Logistics") {
      alert("Driver Supv can only register AMLS trucks.");
      return;
    }
    if (!approvedTruck) {
      alert("Please select a truck from the approved fleet list.");
      return;
    }

    const driverName = getDriverNameFromLabel(form.driverId);
    if (await checkDriverRestStatus(driverName, form.loading_time)) {
      alert('This Expat driver requires rest or is currently on rest. Trip registration is not allowed.');
      return;
    }

    const activeViolation = checkDriverActiveViolation({
      driver_name: driverName,
      reporting_driver_code: form.reporting_driver_code,
    });

    if (activeViolation) {
      alert(ACTIVE_VIOLATION_WARNING);
      return;
    }

    const activeDfmsDefect = checkTruckActiveDfmsDefect(form.truckId);

    if (activeDfmsDefect) {
      alert(ACTIVE_DFMS_DEFECT_WARNING);
      return;
    }

    setSubmitting(true);
    const token = localStorage.getItem("token");

    try {
      const payload = {
        driver_id: form.driverId,
        reporting_driver_code: form.reporting_driver_code,
        truck_id: approvedTruck.truck_reg,
        carrier: approvedTruck.company_name,
        remarks: form.remarks,
        loading_time: form.loading_time
          ? moment(normalizeToHour(form.loading_time)).format()
          : null,
        vehicle_checklist_id: location.state?.vehicleChecklistId || null,
      };

      if (location.state?.editTrip) {
        await axios.put(
          `${process.env.REACT_APP_BASE_API_URL}/trip-registrations/${location.state.editTrip.id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${process.env.REACT_APP_BASE_API_URL}/trip-registrations`,
          {
            ...payload,
            registration_time: moment().format(),
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      navigate("/trip-registration");
    } catch (err) {
      console.error("Error saving trip:", err);
      
      // Enhanced error message handling
      const serverMessage = 
        err?.response?.data?.message ||
        err?.response?.data?.error || 
        err?.message ||
        "Failed to save trip";
        
      alert(serverMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const checkDropdownPosition = (dropdownElement) => {
    if (!dropdownElement) return;

    const rect = dropdownElement.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    if (spaceBelow < 300 && spaceAbove > spaceBelow) {
      setDropdownPosition("up");
    } else {
      setDropdownPosition("down");
    }
  };

  const renderDriverDropdown = () => {
    const userRole = localStorage.getItem("userrole");
    const isDriver = userRole === "driver";

    return (
      <div className="trp-form-group">
        <label className="trp-form-label">
          {translations['Driver']}<span className="trp-required">*</span>
        </label>
        <div className="custom-dropdown">
          <input
            type="text"
            className={`trp-form-input ${isDriver ? "driver-locked" : ""}`}
            value={form.driverLabel}
            onClick={() => {
              if (!isDriver) {
                setShowDriverDropdown(true);
                setDriverSearchTerm("");
                setSelectedDriverTemp(null);
              }
            }}
            readOnly
            placeholder={isDriver ? translations['Driver locked'] : translations['Click to select driver']}
            required
            style={
              isDriver
                ? { backgroundColor: "#f3f4f6", cursor: "not-allowed" }
                : {}
            }
          />
          {showDriverDropdown && !isDriver && (
            <div
              className={`driver-dropdown-menu ${
                dropdownPosition === "up" ? "dropdown-up" : ""
              }`}
              ref={(el) => el && checkDropdownPosition(el)}
              style={{ width: "400px", maxHeight: "500px" }}
            >
              <div className="driver-search-box">
                <input
                  type="text"
                  placeholder="Search driver name or ID..."
                  value={driverSearchTerm}
                  onChange={(e) => setDriverSearchTerm(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  className="driver-search-input"
                  autoFocus
                />
              </div>
              <div
                className="driver-options-container"
                style={{
                  maxHeight: "350px",
                  overflowY: "auto",
                  padding: "0",
                }}
              >
                {filteredDrivers.length > 0 ? (
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        <th
                          style={{
                            padding: "8px 12px",
                            textAlign: "left",
                            backgroundColor: "#f8fafc",
                            borderBottom: "1px solid #e2e8f0",
                            fontSize: "0.75rem",
                            fontWeight: "600",
                            color: "#475569",
                          }}
                        >
                          Driver Name
                        </th>
                        <th
                          style={{
                            padding: "8px 12px",
                            textAlign: "left",
                            backgroundColor: "#f8fafc",
                            borderBottom: "1px solid #e2e8f0",
                            fontSize: "0.75rem",
                            fontWeight: "600",
                            color: "#475569",
                          }}
                        >
                          Driver ID
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDrivers.map((driver, index) => (
                        <tr
                          key={driver.reporting_driver_code}
                          className={`driver-option ${
                            selectedDriverTemp &&
                            selectedDriverTemp.reporting_driver_code ===
                              driver.reporting_driver_code
                              ? "selected"
                              : ""
                          }`}
                          onClick={() => setSelectedDriverTemp(driver)}
                          style={{
                            cursor: "pointer",
                            backgroundColor:
                              selectedDriverTemp &&
                              selectedDriverTemp.reporting_driver_code ===
                                driver.reporting_driver_code
                                ? "#dbeafe"
                                : index % 2 === 0
                                ? "#ffffff"
                                : "#f8fafc",
                            transition: "background-color 0.2s",
                          }}
                          onMouseEnter={(e) => {
                            if (
                              !selectedDriverTemp ||
                              selectedDriverTemp.reporting_driver_code !==
                                driver.reporting_driver_code
                            ) {
                              e.currentTarget.style.backgroundColor = "#f1f5f9";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (
                              !selectedDriverTemp ||
                              selectedDriverTemp.reporting_driver_code !==
                                driver.reporting_driver_code
                            ) {
                              e.currentTarget.style.backgroundColor =
                                index % 2 === 0 ? "#ffffff" : "#f8fafc";
                            }
                          }}
                        >
                          <td
                            style={{
                              padding: "8px 12px",
                              fontSize: "0.875rem",
                              borderBottom: "1px solid #e2e8f0",
                            }}
                          >
                            {driver.driver_name}
                          </td>
                          <td
                            style={{
                              padding: "8px 12px",
                              fontSize: "0.875rem",
                              color: "#64748b",
                              borderBottom: "1px solid #e2e8f0",
                            }}
                          >
                            {driver.reporting_driver_code}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div
                    className="no-results"
                    style={{
                      padding: "16px",
                      textAlign: "center",
                      color: "#64748b",
                    }}
                  >
                    No drivers found
                  </div>
                )}
              </div>
              <div className="driver-dropdown-actions">
                <button
                  type="button"
                  className="driver-cancel-btn"
                  onClick={() => {
                    setShowDriverDropdown(false);
                    setDriverSearchTerm("");
                    setSelectedDriverTemp(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="driver-select-btn"
                  onClick={handleDriverSelection}
                  disabled={!selectedDriverTemp}
                >
                  Select Driver
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderTruckDropdown = () => (
    <div className="trp-form-group">
      <label className="trp-form-label">
        {translations['Truck']}<span className="trp-required">*</span>
      </label>
      <div className="custom-dropdown">
        <input
          type="text"
          className="trp-form-input"
          value={form.truckId}
          onClick={() => {
            setShowTruckDropdown(true);
            setTruckSearchTerm("");
            setSelectedTruckTemp(null);
          }}
          readOnly
          placeholder="Click to select truck"
          required
        />
        {showTruckDropdown && (
          <div
            className={`driver-dropdown-menu ${
              dropdownPosition === "up" ? "dropdown-up" : ""
            }`}
            ref={(el) => el && checkDropdownPosition(el)}
          >
            <div className="driver-search-box">
              <input
                type="text"
                placeholder="Search truck..."
                value={truckSearchTerm}
                onChange={(e) => setTruckSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                className="driver-search-input"
                autoFocus
              />
            </div>
            <div
              className="driver-options-container"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: "8px",
                padding: "12px",
                maxHeight: "500px",
                overflowY: "auto",
              }}
            >
              {console.log(
                "Current vehicles in dropdown (count):",
                showVehicledata?.scheduledVehicles?.length
              )}
              {showVehicledata?.scheduledVehicles &&
              showVehicledata.scheduledVehicles.length > 0 &&
              showVehicledata.scheduledVehicles.filter(
                (truck) =>
                  truck &&
                  normalizeTruckIdForDropdown(truck).includes(
                    normalizeTruckIdForDropdown(truckSearchTerm)
                  )
              ).length > 0 ? (
                showVehicledata.scheduledVehicles
                  .filter(
                    (truck) =>
                      truck &&
                      normalizeTruckIdForDropdown(truck).includes(
                        normalizeTruckIdForDropdown(truckSearchTerm)
                      )
                  )
                  .map((truck, index) => (
                    <div
                      key={index}
                      className={`driver-option ${
                        selectedTruckTemp === truck ? "selected" : ""
                      }`}
                      onClick={() => setSelectedTruckTemp(truck)}
                      style={{
                        margin: "0",
                        padding: "8px",
                        fontSize: "14px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        minWidth: 0,
                      }}
                    >
                      <div className="driver-name">{truck}</div>
                    </div>
                  ))
              ) : (
                <div className="no-results">No trucks found</div>
              )}
            </div>
            <div className="driver-dropdown-actions">
              <button
                type="button"
                className="driver-cancel-btn"
                onClick={() => {
                  setShowTruckDropdown(false);
                  setTruckSearchTerm("");
                  setSelectedTruckTemp(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="driver-select-btn"
                onClick={() => {
                  if (selectedTruckTemp) {
                    const activeDefect = checkTruckActiveDfmsDefect(selectedTruckTemp);
                    if (activeDefect) {
                      alert(ACTIVE_DFMS_DEFECT_WARNING);
                      setShowTruckDropdown(false);
                      setTruckSearchTerm("");
                      setSelectedTruckTemp(null);
                      return;
                    }
                    setForm((prev) => ({
                      ...prev,
                      truckId: selectedTruckTemp,
                      carrier: getCarrierByTruck(selectedTruckTemp),
                    }));
                  }
                  setShowTruckDropdown(false);
                  setTruckSearchTerm("");
                  setSelectedTruckTemp(null);
                }}
                disabled={!selectedTruckTemp}
              >
                Select Truck
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const DriverRestWarning = () => {
    if (!driverRestStatus?.needsRest) return null;

    return (
      <div
        style={{
          backgroundColor: "#FEF2F2",
          border: "1px solid #FEE2E2",
          borderRadius: "8px",
          padding: "12px",
          marginBottom: "16px",
          color: "#991B1B",
        }}
      >
        <strong>Notice:</strong> This driver needs rest, but trip registration is allowed.
        <br />
        Consecutive work days: {driverRestStatus.consecutiveWorkDays}
        <br />
        Last rest date:{" "}
        {moment(driverRestStatus.lastRestDate).format("DD MMM YYYY")}
      </div>
    );
  };

  const DriverViolationWarning = () => {
    if (!driverViolationStatus) return null;

    return (
      <div className="trp-warning-box trp-warning-box-danger">
        <strong>Warning:</strong> This driver has an active violation. Trip registration is not allowed until the violation is closed.
        <br />
        <span dir="rtl">تحذير: لدى هذا السائق مخالفة نشطة. لا يسمح بتسجيل الرحلة حتى يتم إغلاق المخالفة.</span>
      </div>
    );
  };

  const TruckDfmsDefectWarning = () => {
    if (!truckDfmsDefectStatus) return null;

    return (
      <div className="trp-warning-box trp-warning-box-danger">
        <strong>Warning:</strong> This truck has an active DFMS defect. Trip registration is not allowed until DFMS is working.
        <br />
        <span dir="rtl">تحذير: يوجد عطل نشط في نظام DFMS لهذه الشاحنة. لا يسمح بتسجيل الرحلة حتى يعمل النظام.</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="trp-form-container">
        <div className="trp-loading">
          <div className="trp-loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="trp-form-container">
      <div className="trp-form-wrapper">
        <div className="trp-form-card">
          <div className="trp-form-header">
            <h1 className="trp-form-title">
              {location.state?.editTrip ? translations['Edit Trip'] : translations['Register New Trip']}
            </h1>
            <button
              className="trp-form-close"
              onClick={() => navigate("/trip-registration")}
            >
              ×
            </button>
          </div>

          <div className="trp-form-content">
            {!location.state?.editTrip && location.state?.vehicleChecklistId && (
              <div className="trp-workflow-complete"><strong>✓ Vehicle checklist passed</strong><span>Vehicle inspection verified — Step 2 of 2</span></div>
            )}
            <form onSubmit={handleSubmit}>
              {driverRestStatus?.needsRest && <DriverRestWarning />}
              {driverViolationStatus && <DriverViolationWarning />}
              {truckDfmsDefectStatus && <TruckDfmsDefectWarning />}
              {renderDriverDropdown()}
              {renderTruckDropdown()}

              <div className="trp-form-group">
                <label className="trp-form-label">Carrier</label>
                <input
                  type="text"
                  className="trp-form-input"
                  value={form.carrier}
                  readOnly
                  placeholder="Automatically selected from truck"
                />
              </div>

              <div className="trp-form-group">
                <label className="trp-form-label">{translations['Loading Time']}</label>
                <Flatpickr
                  value={form.loading_time}
                  onChange={(dates) => {
                    const selected = dates && dates[0] ? dates[0] : null;
                    // enforce minutes = 00
                    const normalized = selected ? normalizeToHour(selected) : null;
                    setForm((prev) => ({
                      ...prev,
                      loading_time: normalized,
                    }));
                  }}
                  options={{
                    enableTime: true,
                    dateFormat: "d M Y H:i",
                    time_24hr: true,
                    allowInput: true,
                    clickOpens: true,
                    closeOnSelect: false,
                    minuteIncrement: 60, 
                    plugins: [confirmDatePlugin({})],
                    onClose: function (selectedDates) {
                      // fallback: ensure minute=00 when picker closed
                      if (selectedDates && selectedDates[0]) {
                        const n = normalizeToHour(selectedDates[0]);
                        setForm((prev) => ({ ...prev, loading_time: n }));
                      }
                    },
                  }}
                  className="form-input-addrecord"
                />
              </div>

              <div className="trp-form-group">
                <label className="trp-form-label">{translations['Remarks']}</label>
                <input
                  type="text"
                  className="trp-form-input"
                  name="remarks"
                  value={form.remarks}
                  onChange={handleChange}
                  maxLength={255}
                  placeholder={translations['Enter any remarks...']}
                />
                <div className="trp-char-count">{form.remarks.length}/255</div>
              </div>

              <div className="trp-form-actions">
                <button
                  type="submit"
                  className="trp-btn-primary"
                  disabled={submitting || !!driverViolationStatus || !!truckDfmsDefectStatus}
                >
                  {submitting ? (
                    <>
                      <div className="trp-loading-spinner" />
                      {translations['Registering...']}
                    </>
                  ) : (
                    translations['Register Trip']
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TripRegistrationForm;
